import React, { useState, useEffect } from "react";
import "./ImageAssetDemo.css";
import Header from "../../components/Header";
import LeftMenu from "../../components/LeftMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import {JWT} from "../../constants/local-storage";

export default function ImageAssetDemo() {

  /* ============================
     STATE
  ============================ */

  const [highlightedId, setHighlightedId] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [utilHover, setUtilHover] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ============================
     LAYOUT
  ============================ */

  const thisContainer = {
    position: "fixed",
    display: "flex",
    width: "calc(100% - 60px)",
    left: "60px",
    top: "80px",
    right: "0px",
    bottom: "0px"
  };


  /* ============================
     UTILISATION METRICS
  ============================ */

  const chairCount = assets.filter(a => a.label === "Chair").length;
  const peopleCount = assets.filter(a => a.label === "Person").length;

  const utilisation =
    chairCount > 0
      ? Math.round((peopleCount / chairCount) * 100)
      : 0;

  const radius = 50;
  const circumference = 2 * Math.PI * radius;

  const utilisationOffset =
    circumference - (utilisation / 100) * circumference;

  /* ============================
     DOUGHNUT ANIMATION
  ============================ */

  const [animatedOffset, setAnimatedOffset] =
    useState(() => circumference);

  useEffect(() => {
    if (imageSrc) {
      requestAnimationFrame(() => {
        setAnimatedOffset(utilisationOffset);
      });
    }
  }, [imageSrc, utilisationOffset]);

  /* ============================
     IMAGE UPLOAD
  ============================ */


   function handleUpload(e) {
     const file = e.target.files[0];
     if (!file) return;

     const formData = new FormData();
     formData.append("file", file);
	 
	 let headers = {
	     		'Authorization': localStorage.getItem(JWT)
	     	};

	 setLoading(true);              // 👈 START SPINNER
	 setAssets([]);   			
			
     fetch("/resolutionsoftwareplatform/api/ai/vision/detect", {
       method: "POST",
       body: formData,
	   headers:headers,
	   credentials: 'include'
     })
       .then(res => res.json())
       .then(data => {
         setImageSrc(URL.createObjectURL(file));
         setAssets(data.assets);
       })
       .catch(err => {
         console.error("Vision error", err);
       })
   		.finally(() => {
        setLoading(false);          // 👈 STOP SPINNER
       });
   }

  /* ============================
     GROUPING LOGIC
  ============================ */

  function groupAssetsByType(assetList) {
    const grouped = {};

    assetList.forEach(a => {
      if (!grouped[a.label]) {
        grouped[a.label] = [];
      }
      grouped[a.label].push(a);
    });

    return grouped;
  }

  const groupedAssets = groupAssetsByType(assets);

  function toggleGroup(label) {
    setExpandedGroups(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };
 


  /* ============================
     RENDER
  ============================ */

  return (
    <div style={thisContainer}>
      <Header />
      <LeftMenu />

      {/* ================= IMAGE PANEL ================= */}
      <div className="ai-image-panel">
        <div className="ai-image-wrapper">

          {!imageSrc && (
            <label className="ai-upload-placeholder">
              <div className="ai-upload-icon">📷</div>
              <div className="ai-upload-text">Upload an image</div>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleUpload}
              />
            </label>
          )}

          {imageSrc && (
            <>
              <img src={imageSrc} alt="Uploaded" />

              {/* BOUNDING BOXES */}
              {assets.map(a => (
                <div
                  key={a.id}
                  className={
                    "ai-box " +
                    (highlightedId === a.id ||
                     highlightedId === a.label ||
                     (utilHover && (a.label === "Person" || a.label === "Chair"))
                      ? "highlight"
                      : "")
                  }
                  style={{
                    left: `${a.box.x * 100}%`,
                    top: `${a.box.y * 100}%`,
                    width: `${a.box.w * 100}%`,
                    height: `${a.box.h * 100}%`
                  }}
                />
              ))}

              {/* UTILISATION DOUGHNUT */}
              <div
                className="ai-utilisation-card"
                onMouseEnter={() => setUtilHover(true)}
                onMouseLeave={() => setUtilHover(false)}
              >
                <div className="ai-utilisation-title">
                  Seat utilisation
                </div>

                <svg width="120" height="120">
                  <circle
                    cx="60"
                    cy="60"
                    r={radius}
                    stroke="#222"
                    strokeWidth="10"
                    fill="none"
                  />

                  <circle
                    className="ai-donut-ring"
                    cx="60"
                    cy="60"
                    r={radius}
                    stroke="#4285F4"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={animatedOffset}
                    transform="rotate(-90 60 60)"
                  />

                  <text
                    x="50%"
                    y="52%"
                    textAnchor="middle"
                    fill="white"
                    fontSize="22"
                    fontWeight="700"
                  >
                    {utilisation}%
                  </text>
                </svg>

                <div className="ai-utilisation-sub">
                  {peopleCount} people / {chairCount} seats
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ================= ASSET PANEL ================= */}
      <div className="ai-asset-panel">
        <div className="ai-asset-header-row">
          <div className="ai-asset-header">Assets detected</div>

          <label className="ai-upload-button">
            Upload
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleUpload}
            />
          </label>
        </div>

        {Object.keys(groupedAssets).map(label => {
          const group = groupedAssets[label];
          const isOpen = expandedGroups[label];

          return (
            <div key={label} className="ai-asset-group">

              <div
                className="ai-asset-group-header"
                onClick={() => toggleGroup(label)}
                onMouseEnter={() => setHighlightedId(label)}
                onMouseLeave={() => setHighlightedId(null)}
              >
                <div className="ai-asset-group-left">
				<FontAwesomeIcon
				  icon={isOpen ? faAngleDown : faAngleRight}
				  className="ai-chevron"
				/>
                  {label}s
                </div>

                <div className="ai-asset-count">
                  {group.length}
                </div>
              </div>

              {isOpen && (
                <div className="ai-asset-group-items">
                  {group.map((a, index) => (
                    <div
                      key={a.id}
                      className="ai-asset-item"
                      onMouseEnter={() => setHighlightedId(a.id)}
                      onMouseLeave={() => setHighlightedId(null)}
                    >
                      {label} {index + 1}
                    </div>
                  ))}
                </div>
              )}

            </div>
          );
        })}
      </div>
	  {loading && (
	    <div className="ai-loading-overlay">
	      <div className="ai-spinner"></div>
	      <div className="ai-loading-text">
	        Analysing image…
	      </div>
	    </div>
	  )}
    </div>
  );
}
